/**
 * Open a saved conversation: fetch its detail (messages already PII-detokenized
 * by the backend), load them into the working draft, and mark it active. This is
 * the React analogue of chat.js's `loadConversation(id)`.
 *
 * Uses the query client's `ensureQueryData` so an already-cached detail is
 * reused (no refetch) but a cold open still fetches — the same data-access seam
 * the list + detail hooks use, never a raw fetch.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSessionStore } from '@/features/chat/store/sessionStore';
import type { SessionMessage } from '@/features/chat/types';
import { conversationQueries } from './conversations-query';

/** The latest open request wins, whatever order the details arrive in. */
let latestOpen = 0;

export function useOpenConversation() {
  const queryClient = useQueryClient();
  const { setActiveConversation, loadDraft } = useSessionStore();

  return useCallback(
    async (id: string) => {
      // The id and its messages are installed TOGETHER once the detail
      // arrives (no optimistic id switch): otherwise a send while B loads
      // would pair the still-visible A draft with B's id (Codex Phase 2
      // pass 4, #1). If the user navigates again meanwhile (New, another
      // conversation), the late detail is dropped.
      const epoch = useSessionStore.getState().draftEpoch;
      const request = ++latestOpen;
      try {
        const detail = await queryClient.ensureQueryData(
          conversationQueries.detail(id),
        );
        if (
          request !== latestOpen ||
          useSessionStore.getState().draftEpoch !== epoch
        ) {
          return;
        }
        const messages = (detail.messages ?? []) as SessionMessage[];
        setActiveConversation(id);
        loadDraft(messages);
      } catch {
        toast.error('Could not load that conversation.');
      }
    },
    [queryClient, setActiveConversation, loadDraft],
  );
}
